import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import styled, { createGlobalStyle } from 'styled-components';
import { useScroll, useMotionValueEvent } from 'framer-motion';

/*
  Mapa regional "como chegar" — Riviera fixa no centro, veias (rodovias) crescem
  scroll-driven até as cidades-alvo com tempo de carro. Adaptado do EntornoMapa da
  Sofia (SPEC_50), mas paleta clara/náutica e cidades distantes (rotas OSRM reais
  em public/riviera/routes.json). Veias azul→cyan, pins laranja (vela do logo).
*/

const RIV_COORDS = [-48.9631, -23.3509];

const LANDMARKS = [
  { id: 'riviera',      label: 'Riviera',      coords: RIV_COORDS,            hero: true },
  { id: 'Avaré',        label: 'AVARÉ',        coords: [-48.9176, -23.1030], detail: '40 MIN', labelPosition: 'top' },
  { id: 'Itapetininga', label: 'ITAPETININGA', coords: [-48.0488, -23.5891], detail: '1H' },
  { id: 'Bauru',        label: 'BAURU',        coords: [-49.0587, -22.3145], detail: '2H', labelPosition: 'top' },
  { id: 'Sorocaba',     label: 'SOROCABA',     coords: [-47.4526, -23.5015], detail: '2H' },
  { id: 'Campinas',     label: 'CAMPINAS',     coords: [-47.0626, -22.9099], detail: '2H50', labelPosition: 'top' },
  { id: 'São Paulo',    label: 'SÃO PAULO',    coords: [-46.6333, -23.5505], detail: '3H' },
];

const MarkerStyles = createGlobalStyle`
  .riv-marker {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    cursor: default; will-change: opacity, transform;
    opacity: 0; transform: translateY(10px);
    transition: opacity .7s cubic-bezier(0.16,1,0.3,1), transform .7s cubic-bezier(0.16,1,0.3,1);
  }
  .riv-marker.visible { opacity: 1; transform: translateY(0); }
  .riv-marker.label-top { flex-direction: column-reverse; }
  .riv-marker:not(.hero) .label {
    opacity: 0; transform: translateY(4px); pointer-events: none;
    transition: opacity .7s cubic-bezier(0.16,1,0.3,1), transform .7s cubic-bezier(0.16,1,0.3,1);
  }
  .riv-marker:not(.hero).label-visible .label { opacity: 1; transform: translateY(0); }

  .riv-marker .pin {
    width: 12px; height: 12px; background: #F58220; border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px rgba(245,130,32,.35), 0 2px 8px rgba(0,40,80,.4);
  }
  .riv-marker .label { display: flex; flex-direction: column; align-items: center; gap: 1px; line-height: 1.1; }
  .riv-marker .label .name {
    font-family: 'Inter', system-ui, sans-serif; font-size: 11px; font-weight: 700;
    letter-spacing: 0.16em; color: #0a3d6b; text-transform: uppercase; white-space: nowrap;
    text-shadow: 0 0 4px #fff, 0 0 8px #fff, 0 1px 2px rgba(255,255,255,.9);
  }
  .riv-marker .label .detail {
    font-family: 'Inter', system-ui, sans-serif; font-size: 11px; font-weight: 700;
    letter-spacing: 0.1em; color: #F58220; white-space: nowrap;
    text-shadow: 0 0 4px #fff, 0 0 8px #fff;
  }

  /* Riviera hero: marca italic + bolinha grande */
  .riv-marker.hero { gap: 7px; }
  .riv-hero-name {
    font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-weight: 700;
    font-size: 30px; letter-spacing: 0.01em; color: #005BAA; line-height: 1; position: relative;
    text-shadow: 0 0 6px #fff, 0 0 14px #fff, 0 0 28px rgba(255,255,255,.95), 0 2px 6px rgba(255,255,255,.8);
    @media (min-width: 768px) { font-size: 40px; }
  }
  .riv-marker.hero .pin {
    width: 18px; height: 18px; background: #F58220; border: 3px solid #fff;
    box-shadow: 0 0 0 4px rgba(245,130,32,.3), 0 0 18px rgba(245,130,32,.6), 0 2px 10px rgba(0,40,80,.5);
  }
`;

const Section = styled.section`
  position: relative; width: 100%; height: 88vh; min-height: 560px;
  background: #eaf4fb; overflow: hidden;
  @media (min-width: 768px) { height: 92vh; }
`;
const MapContainer = styled.div`
  position: absolute; inset: 0;
  .maplibregl-ctrl, .maplibregl-ctrl-attrib { display: none !important; }
`;
const Fade = styled.div`
  position: absolute; inset: 0; pointer-events: none; z-index: 1;
  background: linear-gradient(180deg,
    #f7fbfd 0%, rgba(247,251,253,0) 12%,
    rgba(247,251,253,0) 86%, #f7fbfd 100%);
`;
const TopLabel = styled.div`
  position: absolute; top: 34px; left: 0; right: 0; z-index: 3; text-align: center;
  pointer-events: none;
  .kick { font-size: 11px; font-weight: 600; letter-spacing: 0.36em; text-transform: uppercase; color: #F58220; }
  .h { font-family: 'Cormorant Garamond', serif; font-weight: 700; color: #005BAA;
       font-size: clamp(26px, 5.6vw, 44px); margin-top: 6px; line-height: 1.05;
       text-shadow: 0 0 10px #eaf4fb, 0 0 24px #eaf4fb; }
  @media (min-width: 768px) { top: 52px; }
`;

export default function RivieraMapa() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const sectionRef = useRef(null);
  const markersRef = useRef([]);
  const [, setReady] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: 'https://tiles.openfreemap.org/styles/positron',
      center: [-47.8, -23.0],
      zoom: 7,
      attributionControl: false,
      interactive: false,
    });
    mapRef.current = map;

    map.on('load', () => {
      // No mobile, esconde Avaré (fica colando em cima da Riviera no centro)
      const isMobileMap = typeof window !== 'undefined' && window.innerWidth < 760;
      const VISIBLE = isMobileMap ? LANDMARKS.filter(l => l.id !== 'Avaré') : LANDMARKS;

      // enquadra toda a região (Riviera + cidades visíveis)
      const lons = VISIBLE.map(l => l.coords[0]);
      const lats = VISIBLE.map(l => l.coords[1]);
      map.fitBounds(
        [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
        { padding: { top: 130, bottom: 80, left: 60, right: 60 }, duration: 0 }
      );

      // Esconde TODOS os labels do mapa base (symbol layers) pra não duplicar
      // os nomes das cidades que já marcamos com nossos próprios markers.
      try {
        map.getStyle().layers.forEach(l => {
          if (l.type === 'symbol') map.setLayoutProperty(l.id, 'visibility', 'none');
        });
      } catch (e) {}

      map.addSource('route', {
        type: 'geojson', lineMetrics: true,
        data: { type: 'FeatureCollection', features: [] },
      });
      const initial = ['interpolate', ['linear'], ['line-progress'], 0, 'rgba(0,185,242,0)', 1, 'rgba(0,185,242,0)'];
      map.addLayer({
        id: 'route-halo', type: 'line', source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-width': 8, 'line-opacity': 0.5, 'line-blur': 4, 'line-gradient': initial },
      });
      map.addLayer({
        id: 'route-line', type: 'line', source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-width': 3, 'line-opacity': 1, 'line-blur': 0.3, 'line-gradient': initial },
      });

      VISIBLE.forEach(({ id, label, coords, hero, detail, labelPosition }) => {
        const el = document.createElement('div');
        const cls = ['riv-marker'];
        if (hero) cls.push('hero', 'visible');
        if (labelPosition === 'top') cls.push('label-top');
        el.className = cls.join(' ');
        if (hero) {
          el.innerHTML = `<div class="riv-hero-name">Riviera</div><div class="pin"></div>`;
        } else {
          el.innerHTML = `<div class="pin"></div><div class="label"><div class="name">${label}</div>${detail ? `<div class="detail">${detail}</div>` : ''}</div>`;
        }
        const isTop = labelPosition === 'top';
        const anchor = hero ? 'bottom' : (isTop ? 'bottom' : 'top');
        const offset = hero ? [0, 9] : (isTop ? [0, 6] : [0, -6]);
        new maplibregl.Marker({ element: el, anchor, offset }).setLngLat(coords).addTo(map);
        markersRef.current.push({ el, isHero: !!hero });
      });

      setReady(true);

      fetch('/riviera/routes.json')
        .then(r => r.json())
        .then(fc => {
          if (isMobileMap && fc?.features) {
            fc.features = fc.features.filter(f => f.properties?.id !== 'Avaré');
          }
          if (mapRef.current?.getSource('route') && fc?.features) {
            mapRef.current.getSource('route').setData(fc);
          }
        })
        .catch(() => {});
    });

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  // Reveal scroll-driven (scrub): veias + nomes desenham conforme rola. Janela ADIANTADA
  // — completa por ~0.3 (antes 0.5/0.6 → demorava demais), mas segue preso ao scroll.
  useMotionValueEvent(scrollYProgress, 'change', (progress) => {
    const nonHero = markersRef.current.filter(m => !m.isHero);
    const N = nonHero.length;
    if (!N) return;

    // No mobile, comprime tudo pra completar mais cedo (menos scroll necessário)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 760;

    // 1) Bolinhas (dots) primeiro, bem cedo
    const pStart = 0.06, pEnd = isMobile ? 0.20 : 0.26, pSpan = pEnd - pStart;
    nonHero.forEach((m, idx) => {
      const t = pStart + (idx / Math.max(N - 1, 1)) * pSpan;
      m.el.classList.toggle('visible', progress >= t);
    });
    // 2) Nomes logo atrás dos dots
    const lStart = 0.10, lEnd = isMobile ? 0.26 : 0.34, lSpan = lEnd - lStart;
    nonHero.forEach((m, idx) => {
      const t = lStart + (idx / Math.max(N - 1, 1)) * lSpan;
      m.el.classList.toggle('label-visible', progress >= t);
    });

    // 3) Veias DEPOIS: a rota cresce do Riviera até a cidade que JÁ apareceu (dot+nome)
    const vStart = isMobile ? 0.20 : 0.26;
    const vEnd = isMobile ? 0.46 : 0.62;
    const draw = Math.max(0, Math.min((progress - vStart) / (vEnd - vStart), 1));
    if (!mapRef.current?.getLayer('route-line')) return;
    let g;
    if (draw >= 1) g = ['interpolate', ['linear'], ['line-progress'], 0, 'rgba(0,185,242,1)', 1, 'rgba(0,91,170,1)'];
    else if (draw <= 0) g = ['interpolate', ['linear'], ['line-progress'], 0, 'rgba(0,185,242,0)', 1, 'rgba(0,185,242,0)'];
    else {
      const e = Math.max(0.001, Math.min(draw, 0.998));
      g = ['interpolate', ['linear'], ['line-progress'],
        0, 'rgba(0,185,242,1)', e, 'rgba(0,91,170,1)',
        e + 0.001, 'rgba(0,91,170,0)', 1, 'rgba(0,91,170,0)'];
    }
    try {
      mapRef.current.setPaintProperty('route-line', 'line-gradient', g);
      mapRef.current.setPaintProperty('route-halo', 'line-gradient', g);
    } catch (e) {}
  });

  return (
    <Section ref={sectionRef}>
      <MarkerStyles />
      <MapContainer ref={containerRef} />
      <Fade />
      <TopLabel>
        <div className="kick">Localização</div>
        <div className="h">Pertinho de tudo</div>
      </TopLabel>
    </Section>
  );
}
